import type { DispatchCtx, PinnedVisitor } from '../contexts/QueueDispatchContext';
import { agentLanguageCode, requeueStarted, sameLanguage } from './queue';
import type { ConfigurationRelease, CrashRecovery, StartedEntry } from '../types/database';

// Customer-queue recovery actions shared by PCTile (real agent) and
// FakePCTile (staging simulation): "Opnieuw starten", "Verplaats naar andere
// PC" and "Terug in wachtrij". The tile supplies how to stop and how to
// relaunch; the queue semantics live here so both tiles behave identically:
//  - restart: stop, then relaunch the same experience/language pinned to the
//    same queue row (fn_dequeue_entry on a started row = no-op move).
//  - move: stop, then open the dispatch pop-up pinned to the visitor and keep
//    a pendingMove banner until the row leaves 'started'.
//  - requeue: stop, then fn_requeue_started puts the row back at its spot.
// Every stop goes through suppressAutoOpen first: the PC is freed on purpose,
// so the running -> free trigger must not open the pop-up on top of us.

// --- Crash recovery ladder --------------------------------------------------
// The tile's reading of the agent's crash_recovery block, shared by PCTile and
// FakePCTile so the staging drill walks exactly the same rungs as a real PC:
//   restarting -> amber. The agent is putting the SAME run back on this PC;
//                 the visitor has not left, so no recovery buttons and no
//                 "Volgende bezoeker" pop-up.
//   gave_up    -> red. The loop guard stopped relaunching; the operator
//                 resolves the visitor with the three recovery actions.
// A healthy PC (or an agent too old to send the block) yields null and the
// tile renders exactly as it did before — show by exception.
export type CrashRecoveryTone = 'restarting' | 'gave_up';

export interface CrashRecoveryView {
  tone: CrashRecoveryTone;
  /** Dutch headline for the tile. */
  text: string;
}

export function crashRecoveryView(cr: CrashRecovery | null | undefined): CrashRecoveryView | null {
  if (!cr) return null;
  if (cr.restarting) {
    // The relaunch guard is shared with the freeze ladder: a relaunch with no
    // crash on record means the experience hung, it did not crash.
    return {
      tone: 'restarting',
      text:
        cr.crashes_in_window > 0
          ? `Gecrasht, automatisch herstarten (poging ${cr.crashes_in_window})`
          : 'Vastgelopen, automatisch herstarten',
    };
  }
  if (cr.gave_up) {
    return { tone: 'gave_up', text: `Auto-herstel gestopt na ${Math.max(1, cr.crashes_in_window)} crashes` };
  }
  return null;
}

export function currentOperatorId(): string | undefined {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : undefined;
  } catch {
    return undefined;
  }
}

/** Language to relaunch with: what we dispatched (same visitor, this PC) else
 *  the departure's play language, but only when the release supports it. */
export function relaunchLanguage(args: {
  experiences: ConfigurationRelease[];
  experienceId: string;
  visitor: StartedEntry;
  pcId: string;
  lastDispatch: DispatchCtx['lastDispatch'] | null | undefined;
}): string | null {
  const { experiences, experienceId, visitor, pcId, lastDispatch: ld } = args;
  const rel = experiences.find((e) => e.experience_id === experienceId);
  const codes = (rel?.experiences?.supported_languages ?? [])
    .map((sl) => sl.language?.short_name ?? null)
    .filter((c): c is string => !!c);
  if (codes.length === 0) return null;
  const wanted =
    ld && ld.queueEntryId === visitor.queue_entry_id && ld.pcId === pcId && ld.language
      ? ld.language
      : agentLanguageCode(visitor.play_language);
  return codes.find((c) => sameLanguage(c, wanted)) ?? (codes.length === 1 ? codes[0] : null);
}

export interface RunningExperience {
  id: string;
  version: string;
}

export interface TileRecoveryDeps {
  pcId: string;
  /** Machine name, for records and tooltips. */
  pcName: string;
  /** What the operator sees on that PC's bar (bare index, or "2: MONA-02" for
   *  an admin). Banners use this so they match the dashboard behind them. */
  pcLabel: string;
  /** The tile's launchable releases (for the relaunch language lookup). */
  experiences: ConfigurationRelease[];
  dispatchCtx: DispatchCtx | null;
  queueCtx: { refresh: () => void } | null;
  /** Stop without a confirm prompt. Resolves true once the experience is gone;
   *  the tile reports its own error on false. */
  stop: () => Promise<boolean>;
  /** Relaunch through the tile's own launch flow, pinned to the visitor's row. */
  relaunch: (
    experienceId: string,
    version: string,
    language: string | null,
    dequeueEntryId: string,
  ) => Promise<boolean>;
  setBusy: (busy: boolean) => void;
  setError: (message: string | null) => void;
  /** A real agent needs a moment to reap the process before a relaunch. */
  relaunchDelayMs: number;
}

const SUPPRESS_AUTO_OPEN_MS = 30_000;

async function silentStop(deps: TileRecoveryDeps): Promise<boolean> {
  deps.dispatchCtx?.suppressAutoOpen(deps.pcId, SUPPRESS_AUTO_OPEN_MS);
  return deps.stop();
}

/** "Opnieuw starten": broken start -> same experience, same language, same
 *  queue row. */
export async function queueRestart(
  deps: TileRecoveryDeps,
  visitor: StartedEntry,
  current: RunningExperience,
): Promise<void> {
  const language = relaunchLanguage({
    experiences: deps.experiences,
    experienceId: current.id,
    visitor,
    pcId: deps.pcId,
    lastDispatch: deps.dispatchCtx?.lastDispatch,
  });
  deps.setBusy(true);
  deps.setError(null);
  const killed = await silentStop(deps);
  deps.setBusy(false);
  if (!killed) return;
  if (deps.relaunchDelayMs > 0) {
    await new Promise((r) => setTimeout(r, deps.relaunchDelayMs));
  }
  await deps.relaunch(current.id, current.version, language, visitor.queue_entry_id);
}

/** "Verplaats naar andere PC": stop here, then the dispatch pop-up opens
 *  pinned to this visitor; the other tile's launch moves started_pc_id. */
export async function queueMove(
  deps: TileRecoveryDeps,
  visitor: StartedEntry,
  current: RunningExperience | null,
): Promise<void> {
  const dispatchCtx = deps.dispatchCtx;
  if (!dispatchCtx) return;
  const pinned: PinnedVisitor = {
    entry: visitor,
    fromPcId: deps.pcId,
    fromPcName: deps.pcName,
    fromPcLabel: deps.pcLabel,
    experienceId: current?.id ?? null,
    language: current
      ? relaunchLanguage({
          experiences: deps.experiences,
          experienceId: current.id,
          visitor,
          pcId: deps.pcId,
          lastDispatch: dispatchCtx.lastDispatch,
        })
      : null,
  };
  deps.setBusy(true);
  deps.setError(null);
  const killed = await silentStop(deps);
  deps.setBusy(false);
  if (!killed) return;
  dispatchCtx.setPendingMove(pinned);
  dispatchCtx.openDispatch(null, { pinned });
}

/** "Terug in wachtrij": stop here, row back to queued at its original spot. */
export async function queueRequeue(deps: TileRecoveryDeps, visitor: StartedEntry): Promise<void> {
  const operatorId = currentOperatorId();
  if (!operatorId) return;
  deps.setBusy(true);
  deps.setError(null);
  const killed = await silentStop(deps);
  if (killed) {
    try {
      await requeueStarted(visitor.queue_entry_id, operatorId);
      deps.queueCtx?.refresh();
    } catch (err) {
      deps.setError((err as { message?: string })?.message || 'Terug in wachtrij mislukt');
    }
  }
  deps.setBusy(false);
}
